import { useTranslation } from 'react-i18next';

import type { IdUserDto } from '@/services/ws-companies/src/main/resources/rest-api';
import { getUserById } from '@/services/ws-companies/src/main/resources/rest-api';
import { getAccountShortById } from '@/services/ws-crm/src/main/resources/rest-api';
import type { CampaignStatus } from '@/services/ws-digital/src/main/resources/rest-api';
import type { IdAccountDto } from '@/services/ws-orders/src/main/resources/rest-api';
import { CampaignPositionType, getOwnerList, getCampaignTypes, getStages } from '@/services/ws-orders/src/main/resources/rest-api';
import { createContexts } from '@/js/libs/create-contexts';
import { useLazyRequest } from '@/js/libs/use-request';

import { useFilters, createStringFilterConfig, createDateRangeFilterConfig } from 'src/hooks/useFilters/index.tsx';
import { StorageScope } from 'src/hooks/useSessionStorage.ts';
import { ErrorCode } from 'src/types.ts';
import { useDefaultPositionTypeStatus } from '../../hooks/useDefaultPositionTypeStatus.ts';
import { AMOUNT_COLUMNS, DEFAULT_AMOUNT_COLUMN, type AmountColumn } from './hooks/useColumns/index.tsx';
import type { FiltersState } from './types.ts';

const BROADCAST_STORAGE_SCOPE: Partial<Record<CampaignPositionType, StorageScope>> = {
  [CampaignPositionType.RADIO]: StorageScope.BROADCAST_POSITIONS_RADIO,
  [CampaignPositionType.TV]: StorageScope.BROADCAST_POSITIONS_TV,
};

export const { useBroadcastFiltersContext } = createContexts({
  BroadcastFilters({
    campaignStatuses,
    broadcastType,
  }: {
    campaignStatuses: CampaignStatus[];
    broadcastType: CampaignPositionType.RADIO | CampaignPositionType.TV;
  }) {
    const [t] = useTranslation(['common']);
    const { defaultPositionTypeStatus } = useDefaultPositionTypeStatus();

    const getAccountShortByIdRequest = useLazyRequest({
      apiMethod: getAccountShortById,
      ignoreErrors: [ErrorCode.NotFound],
    });

    const getOwnerListRequest = useLazyRequest({
      apiMethod: getOwnerList,
      queryDefaults: false,
      ignoreErrors: [ErrorCode.NotFound],
    });

    const getCampaignTypesRequest = useLazyRequest({
      apiMethod: getCampaignTypes,
      ignoreErrors: [ErrorCode.NotFound],
    });

    const getUserByIdRequest = useLazyRequest({
      apiMethod: getUserById,
      ignoreErrors: [ErrorCode.NotFound],
    });

    const getStagesRequest = useLazyRequest({
      apiMethod: getStages,
      ignoreErrors: [ErrorCode.NotFound],
    });

    const { filters$, multiFilter$, updateFilter, isFilterHidden, areFiltersPreloading$, MultiFilter, ClearFiltersButton } =
      useFilters<FiltersState>({
        storageScope: BROADCAST_STORAGE_SCOPE[broadcastType],
        filterConfigs: {
          statusStage: {
            defaultValue: defaultPositionTypeStatus,
            emptyValue: defaultPositionTypeStatus,
            disabledStoragePersist: true,
            isMainFilter: true,
            compare: (a, b) => a === b,
            serialize: (value) => value,
            syncDeserialize: (stored) => campaignStatuses.find((item) => item === stored) ?? null,
          },
          search: createStringFilterConfig({ isMainFilter: true }),
          invoicingAccountId: {
            defaultValue: null,
            emptyValue: null,
            getLabel: () => t('common:INVOICING_ACCOUNT_FILTER_LABEL'),
            compare: (a, b) => a?.value === b?.value,
            serialize: (option) => option?.value.toString() ?? null,
            asyncDeserialize: async (stored) => {
              const { data } = await getAccountShortByIdRequest.load({ accountId: stored as unknown as IdAccountDto });
              if (data?.id) {
                return { label: data.displayName ?? '', value: data.id, active: data.active };
              }
              return null;
            },
          },
          owner: {
            defaultValue: null,
            emptyValue: null,
            getLabel: () => t('common:SALES'),
            compare: (a, b) => a?.value === b?.value,
            serialize: (option) => option?.value ?? null,
            asyncDeserialize: async (stored) => {
              const { data } = await getOwnerListRequest.load({});
              const owner = data?.items.find((item) => item.ownerId === stored);
              if (owner && owner.ownerId) {
                return { label: owner.name ?? '', value: owner.ownerId };
              }
              return null;
            },
          },
          accountId: {
            defaultValue: null,
            emptyValue: null,
            getLabel: () => t('common:REAL_ACCOUNT'),
            compare: (a, b) => a?.value === b?.value,
            serialize: (option) => option?.value.toString() ?? null,
            asyncDeserialize: async (stored) => {
              const { data } = await getAccountShortByIdRequest.load({ accountId: stored as unknown as IdAccountDto });
              if (data?.id) {
                return { label: data.displayName ?? '', value: data.id, active: data.active };
              }
              return null;
            },
          },
          stages: {
            defaultValue: null,
            emptyValue: null,
            getLabel: () => t('common:STAGES'),
            compare: (a, b) => JSON.stringify(a?.map((item) => item.value).sort()) === JSON.stringify(b?.map((item) => item.value).sort()),
            serialize: (options) => options?.map((option) => option.value).join(',') ?? null,
            asyncDeserialize: async (stored) => {
              if (!stored) return null;
              const { data } = await getStagesRequest.load({});
              const stageValues = stored.split(',').filter(Boolean);
              const validStages = data?.filter((stage) => stageValues.includes(stage)) ?? [];
              return validStages.map((value) => ({ label: value, value }));
            },
          },
          campaignTypeName: {
            defaultValue: null,
            emptyValue: null,
            getLabel: () => t('common:CAMPAIGN_TYPE'),
            compare: (a, b) => a?.value === b?.value,
            serialize: (option) => option?.value ?? null,
            asyncDeserialize: async (stored) => {
              const { data } = await getCampaignTypesRequest.load({});
              const campaignType = data?.find((item) => item.id === stored);
              if (campaignType && campaignType.id) {
                return { label: campaignType.name ?? '', value: campaignType.id };
              }
              return null;
            },
          },
          createdByUserId: {
            defaultValue: null,
            emptyValue: null,
            getLabel: () => t('common:CREATED_BY'),
            compare: (a, b) => a?.value === b?.value,
            serialize: (option) => option?.value?.toString() ?? null,
            asyncDeserialize: async (stored) => {
              const { data } = await getUserByIdRequest.load({ id: stored as unknown as IdUserDto });
              if (data) {
                return { label: data.name ?? `${data.firstName} ${data.lastName}`, value: data.userId };
              }
              return null;
            },
          },
          amountColumn: {
            defaultValue: DEFAULT_AMOUNT_COLUMN,
            emptyValue: DEFAULT_AMOUNT_COLUMN,
            isMainFilter: true,
            compare: (a, b) => a === b,
            serialize: (value) => value,
            syncDeserialize: (stored) =>
              AMOUNT_COLUMNS.includes(stored as AmountColumn) ? (stored as AmountColumn) : DEFAULT_AMOUNT_COLUMN,
          },
          startDateRange: createDateRangeFilterConfig({
            getLabel: () => t('common:START_DATE'),
          }),
          endDateRange: createDateRangeFilterConfig({
            getLabel: () => t('common:END_DATE'),
          }),
        },
      });

    return {
      filters$,
      multiFilter$,
      updateFilter,
      isFilterHidden,
      areFiltersPreloading$,
      MultiFilter,
      ClearFiltersButton,
    };
  },
});
